import { Box, createStyles, LoadingOverlay } from '@mantine/core';
import React, { useMemo } from 'react';
import { Redirect } from 'react-router-dom';

import { DeviceDetailsType, DeviceModelType } from '@portals/api/organizations';
import { DeviceType as CommonDeviceType } from '@portals/types';

import { useRouteModalConfig } from '../../route-modals.hooks';
import { RouteModal } from '../../RouteModal';
import { useDeviceContext } from './context';
import { DeviceInfo } from './DeviceInfo';
import { DeviceTabs } from './DeviceTabs';

export interface DeviceProps<
  TDevice extends DeviceDetailsType | CommonDeviceType,
  TDeviceModel extends DeviceModelType
> {
  model: TDeviceModel;
  device: TDevice;
  isLoading?: boolean;
}

export function Device<
  TDevice extends DeviceDetailsType | CommonDeviceType,
  TDeviceModel extends DeviceModelType
>({ device, isLoading = false, model }: DeviceProps<TDevice, TDeviceModel>) {
  const contentStyles = useStyles();
  const defaultBackgroundPath = useRouteModalConfig('device').backgroundPath;
  const { isUpdatingDevice } = useDeviceContext();

  const content = useMemo(() => {
    if (isLoading) return <LoadingOverlay visible />;
    else if (!device || !model) return <Redirect to={defaultBackgroundPath} />;

    return (
      <>
        <LoadingOverlay visible={isUpdatingDevice} />
        <DeviceInfo device={device} model={model} />
        <DeviceTabs device={device} model={model} />
      </>
    );
  }, [defaultBackgroundPath, isLoading, isUpdatingDevice, model, device]);

  return (
    <RouteModal modalId="device">
      <Box className={contentStyles.classes.container}>{content}</Box>
    </RouteModal>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '385px 1fr',

    [theme.fn.largerThan(1600)]: {
      gridTemplateColumns: '538px 1fr',
    },
  },
}));
